import { Component, DestroyRef, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { UserConfigService } from './services/user-config.service';
import { UserConfig } from './model/reservation.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { PolicyDialogComponent } from './pages/cookies-policy/policy-dialog/policy-dialog.component';
import { CookieService } from 'ngx-cookie-service';
import { CookiesDialogResult } from './model/cookies.model';
import { LoaderService } from './services/loader.service';
import { NavigationEnd, Router } from '@angular/router';
import Hotjar from '@hotjar/browser';
import Keycloak from 'keycloak-js';
import { ReservationService } from './services/reservation.service';
import { environment } from '../environments/environment';
import { AirspaceService } from './services/airspace.service';
import { AdhocFormService } from './services/adhoc-form.service';
import { filter } from 'rxjs';
import { LayoutUtilsService } from './services/layout-utils.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HeaderComponent } from './ui/header/header.component';
import { HolidaysInfoComponent } from './ui/holidays-info/holidays-info.component';
import { NotesComponent } from './ui/notes/notes.component';
import { ChangesPopupComponent } from './ui/changes-popup/changes-popup.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

const hotjarVersion = 6;

@Component({
  selector: 'rqa-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [PrimeNG],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    HeaderComponent,
    HolidaysInfoComponent,
    NotesComponent,
    ChangesPopupComponent,
    ProgressSpinnerModule,
    ToastModule,
    ConfirmDialogModule
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  translateService = inject(TranslateService);
  router = inject(Router);
  config = inject(PrimeNG);
  userConfigService = inject(UserConfigService);
  dialogService = inject(DialogService);
  cookieService = inject(CookieService);
  reservationService = inject(ReservationService);
  airspaceService = inject(AirspaceService);
  adhocFormServive = inject(AdhocFormService);
  layoutUtilsService = inject(LayoutUtilsService);
  destroyRef = inject(DestroyRef);
  keycloak = inject(Keycloak);
  userConfig: UserConfig;
  getScreenWidth: number = window.innerWidth;
  getScreenHeight: number = window.innerHeight;
  isMobileDevice: boolean = false;
  loading: boolean = false;
  isHolidaysInfoVisible = this.reservationService.holidaysInfo;
  cookiesDialogRef: DynamicDialogRef | null = null;

  constructor(private loaderService: LoaderService) {
    this.loaderService.loaderState.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state: boolean) => {
      this.loading = state;
    });
  }

  ngOnInit(): void {
    if (this.cookieService.check('analitycsChecked')) {
      Hotjar.init(environment.siteId, hotjarVersion);
    }
    this.translateService.addLangs(['en', 'pl']);
    this.translateService.setDefaultLang('pl');
    this.getUserConfig();
    this.reservationService.getHolidays().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.reservationService.getCommonReservableInterval().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.translateService
      .get('primeng')
      .pipe(takeUntilDestroyed(this.destroyRef))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((res: any) => {
        this.config.setTranslation(res);
      });
    this.airspaceService.fetchAirspace().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.checkActiveRouterLink();
    this.setLayoutSize();
  }

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.getScreenWidth = window.innerWidth;
    this.getScreenHeight = window.innerHeight;
    this.setLayoutSize();
  }

  translate(lang: string) {
    this.translateService.use(lang);
    this.translateService
      .get('primeng')
      .pipe(takeUntilDestroyed(this.destroyRef))
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe((res: any) => this.config.setTranslation(res));
  }

  private setLayoutSize(): void {
    this.layoutUtilsService.windowSize.set({ width: this.getScreenWidth, height: this.getScreenHeight });
    this.layoutUtilsService.isMobileDevice.set(this.getScreenWidth < 992);
    this.layoutUtilsService.calculateContentHeight();
  }

  private getUserConfig(): void {
    this.userConfigService.getUserConfiguration().subscribe((userConfig) => {
      if (userConfig) {
        this.userConfig = userConfig;
        this.translate(userConfig.locale.toLowerCase());
        setTimeout(() => this.showCookiesDialog(), 1000);
      }
    });
  }

  private showCookiesDialog(): void {
    if (!this.cookieService.check('cookiesPolicy')) {
      this.cookiesDialogRef = this.dialogService.open(PolicyDialogComponent, {
        header: this.translateService.instant('dialogs.cookiePolicy.header'),
        contentStyle: { overflow: 'auto' },
        width: '50vw',
        modal: false,
        position: 'bottom',
        breakpoints: {
          '960px': '50vw',
          '640px': '90vw'
        },
        baseZIndex: 10000
      });

      if (this.cookiesDialogRef) {
        this.cookiesDialogRef.onClose
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((result: CookiesDialogResult) => {
            if (result?.accepted) {
              this.cookieService.set('cookiesPolicy', 'true', 365, '/', '', true, 'Strict');
              if (result?.analitycsChecked) {
                this.cookieService.set('analitycsChecked', 'true', 365, '/', '', true, 'Strict');
              }
            } else {
              this.keycloak.logout({ redirectUri: window.location.origin });
            }
          });
      }
    }
  }

  private checkActiveRouterLink(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        if (event.url !== '/adhoc') {
          this.adhocFormServive.setAdhocData(null);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.cookiesDialogRef) {
      this.cookiesDialogRef.close();
    }
  }
}
